import React, { useCallback, useEffect, useState } from 'react';
import { IconAsChunk } from '@boschebike/ebds-react-icons';

/**
 * @returns {Location | false}
 */
const getLocation = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return location;
};

const matchMenuUrl = (originalItemUrl) => {
  // Exact match pathname === item pathname, this is false when search params are in the item url.
  // We don't compare search params from the url as it might be different
  // (services add some additional ones).
  if (location.pathname === originalItemUrl) {
    return true;
  }

  const itemUrl = new URL(originalItemUrl, location.origin);

  // Pathnames not same. Different page. Not active.
  if (location.pathname !== itemUrl.pathname) {
    return false;
  }

  // Matching search params
  const locationUrl = new URL(location.href);
  const match = [...itemUrl.searchParams.entries()].every(([key, value]) => {
    const paramFromLocation = locationUrl.searchParams.get(key);
    if (paramFromLocation === value) {
      return true;
    }
    return false;
  });

  return match;
};

/**
 * @typedef {import('../App.d.ts').MainNavigationItem} MainNavigationItem
 */
/**
 * @param {MainNavigationItem} entry
 */
const isMenuItemActive = (entry) => {
  const location = getLocation();
  if (location === false) {
    // SSR, nothing is active.
    return false;
  }

  const elMatch = entry.isExternal ? false : matchMenuUrl(entry.url);
  if (elMatch) {
    return true;
  }
  const noSubmenu = (entry?.subMenu?.length ?? 0) < 1;

  if (noSubmenu) {
    return false;
  }

  const childrenMatch = entry.subMenu.some((el) => isMenuItemActive(el));

  return childrenMatch;
};

/**
 * @typedef {import('../App.d.ts').LinkProps} LinkProps
 */
/**
 * @param {LinkProps} props
 */
const Link = (props) => {
  // eslint-disable-next-line consistent-return
  const handleClick = useCallback((event) => {
    const { currentTarget: target } = event;
    const { href } = target;
    const newUrl = new URL(href);
    const currentUrl = new URL(location.href);

    // If link is external...
    if (newUrl.hostname !== currentUrl.hostname) {
      // ...allow browser to handle href natively
      return true;
    }

    // Merge new query params from link with old params that were not in the link.
    for (const [key, value] of currentUrl.searchParams.entries()) {
      if (newUrl.searchParams.has(key)) {
        continue;
      }
      newUrl.searchParams.set(key, value);
    }

    if (newUrl.pathname !== currentUrl.pathname) {
      // Normal link, do nothing special.
      // eslint-disable-next-line consistent-return
      return;
    }

    // Slug didn't change. Push state instead so micro frontends can react.
    event.preventDefault();
    const newState = `${newUrl.pathname}${newUrl.search}`;
    window.history.pushState({}, '', newState);
    // WARNING: current Web API doesn't have any native event that happens
    // when history.pushState happens.
    // There is a proposal but not even in stage yet.
    // https://github.com/whatwg/html/issues/5562
    const customEvent = new CustomEvent('historyupdate');
    window.dispatchEvent(customEvent);
  }, []);

  return (
    <a
      href={props.entry.url}
      target={props.entry.isExternal ? '_blank' : undefined}
      rel={props.entry.isExternal ? 'noreferrer noopener' : undefined}
      onClick={handleClick}
    >
      {props.entry.isExternal && (
        <IconAsChunk iconName="externallink" />
      )}
      {props.entry.label}
    </a>
  );
};

/**
 * @param {MainNavigationItem} entry
 */
const linksIterator = (entry) => ({
  key: `${entry.url}-${entry.label}`,
  // eslint-disable-next-line jsx-a11y/anchor-is-valid
  element: <Link entry={entry} />,
  active: isMenuItemActive(entry),
  subMenu: entry?.subMenu?.length ? entry.subMenu.map(linksIterator) : undefined,
  isExternal: entry.isExternal,
});

/**
 * @param {MainNavigationItem} entry
 */
const initialLinksIterator = (entry) => ({
  key: `${entry.url}-${entry.label}`,
  // eslint-disable-next-line jsx-a11y/anchor-is-valid
  element: <Link entry={entry} />,
  active: false,
  subMenu: entry?.subMenu?.length ? entry.subMenu.map(initialLinksIterator) : undefined,
  isExternal: entry.isExternal,
});

const clearWhenOnlyOneItem = (links) => {
  if (links.length === 1 && !links[0].subMenu) {
    return [];
  }

  return links;
};

export const useNavigationLinks = (navigationLinks) => {
  const [links, setLinks] = useState(navigationLinks.map(initialLinksIterator));

  const parsed = clearWhenOnlyOneItem(links);

  useEffect(() => {
    setLinks(navigationLinks.map(linksIterator));
    // This has to run only once on the client.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const listener = () => {
      setLinks(navigationLinks.map(linksIterator));
    };

    window.addEventListener('historyupdate', listener);
    return () => {
      window.removeEventListener('historyupdate', listener);
    };
  }, [navigationLinks]);

  return parsed;
};
